import { useApi } from "@/hooks";
import KitSelector from "@Components/app/user/kit-selector";
import ContainerWrapper from "@Components/generic/container-wrapper";
import { Events } from "@Context/common/general/context";
import { usePermissions } from "@Context/common/permissions/permissions";
import AccountRoles from "@Enums/AccountRoles";
import VRHeaderButtonType from "@Enums/VRHeaderButtonType";
import useGlobalContext from "@hooks/common/general/use-global-context";
import useI18n from "@i18n";
import { Alert, AlertTitle, Box, CircularProgress, Container, useTheme } from '@mui/material';
import { Api } from "@Shared/schemas";
import { KitRecord } from "@Shared/schemas/avatars/avatarSchemas";
import { isRoleHighOrEqual } from "@Shared/utils/rolePermissions";
import { useEffect, useState } from "react";
import { useUnmount } from "react-use";

const KitEditor = ({ pageId, isAccount = false }: { pageId: string; isAccount?: boolean }) => {
  const { globalState } = useGlobalContext();
  const { i18n } = useI18n();
  const theme = useTheme();
  const permissions = usePermissions?.();

  const [kit, setKit] = useState<KitRecord>({
    id: -1,
    primary: 'grey',
    secondary: 'white',
    tertiary: 'black',
    goalkeeper_primary: 'grey',
    goalkeeper_secondary: 'white',
    goalkeeper_tertiary: 'black',
  } as any);

  const { call: getTeamConfigs, data: teamConfigs } = useApi<typeof Api.Teams.GetTeamConfigs>({
    api: Api.Teams.GetTeamConfigs,
    request: {},
  });

  const { call: updateKit } = useApi<typeof Api.Avatars.UpdateKit>({
    api: Api.Avatars.UpdateKit,
    request: {} as any,
  });

  useEffect(() => {
    Events.app.showHeaderButton.emit({
      type: VRHeaderButtonType.BACK,
      title: i18n(isAccount ? "settings.default-kit.title" : "settings.my-kit.title"),
    });
    getTeamConfigs({
      ...(isAccount
        ? {
            account_id: globalState.account.id!,
          }
        : {
            user_doc_id: globalState.user.id,
          }),
      user_doc_id: globalState.user.id!,
      limit: 1,
    });
  }, []);

  useEffect(() => {
    if (teamConfigs && teamConfigs.length > 0) {
      setKit(teamConfigs[0].home_kit);
    }
  }, [teamConfigs]);

  useUnmount(() => {
    if (!permissions?.canEdit) {
      return;
    }
    if (kit.id === -1) return;
    updateKit({
      kit_id: kit?.id,
      create: false,
      ...kit,
    }).then(() => {
      // Push kit changes to Unity
      const userInfo = { ...globalState.user, kit: kit };
      Events.auth.accountInfoUpdated.emit(userInfo);
    });
  });

  return (
    <ContainerWrapper id={pageId} sx={{ overflow: 'visible' }}>
      <Container
        maxWidth="sm"
        sx={{
          p: { xs: theme.spacing(2, 0), sm: theme.spacing(0, 0, 6) },
          height: { xs: '100%', sm: 'auto' },
        }}
      >
        {(isAccount && !isRoleHighOrEqual(globalState.account.user_role!, AccountRoles.ADMIN)) && (
        <Alert severity='info' sx={{ backgroundColor: 'transparent' }}>
          <AlertTitle>
            {i18n('settings.my-kit.not-admin-warning')}
          </AlertTitle>
        </Alert>
        )}
        <KitSelector gender={globalState.user.gender!} kit={kit} onChange={setKit} width={250} />
        {!teamConfigs && (
          <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
            <CircularProgress />
          </Box>
        )}
      </Container>
    </ContainerWrapper>
  );
};

export default KitEditor;
